/* eslint-disable max-classes-per-file */
import { orderBy } from "lodash";
import { IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { IDagKvantitet } from "~app/common/models/dagkvantitet";
import { ForsorjningskallaAggregat, ForsorjningskallaTyp } from "~app/common/models/forsorjningskalla";
import { LeveransraderAggregat } from "~app/common/models/leveransrad";
import { TaktCalculator } from "~app/common/models/takt-calculator";
import { IOrderHandelssortiment, IOrderMottagningsplats, IOrderProduktionsomraderad } from "./justeraLeveransplanModel";

interface IProduktionsomrade {
    id: number;
    namn: string;
}

export class ProduktionsomradeAggregat {
    produktionsomraderader: ProduktionsomradeRadAggregat[];

    constructor(produktionsomradeIds: number[], raw: IOrderProduktionsomraderad[]) {
        let produktionsomraden = raw
            .reduce((result, produktionsomraderad) => {
                produktionsomraderad.kundorderrad.forsorjningskallor
                    .filter(forsorjningskalla => forsorjningskalla.group === ForsorjningskallaTyp.Produktionsomrade)
                    .forEach(forsorjningskalla => {
                        if (!result[forsorjningskalla.namn]) {
                            const produktionsomrade: IProduktionsomrade = {
                                id: forsorjningskalla.id,
                                namn: forsorjningskalla.namn
                            };

                            // eslint-disable-next-line sonarjs/no-associative-arrays
                            result[forsorjningskalla.namn] = produktionsomrade;
                            result.push(produktionsomrade);
                        }
                    });
                return result;
            }, Array<IProduktionsomrade>());

        // Produktionsområden filtreras på klienten.
        const filterProduktionsomraden = produktionsomradeIds && produktionsomradeIds.length > 0;
        if (filterProduktionsomraden) {
            produktionsomraden = produktionsomraden.filter(produktionsomrade =>
                produktionsomradeIds.some(produktionsomradeId => produktionsomrade.id === produktionsomradeId));
        }

        const produktionsomradeRadAggregat = produktionsomraden.map(x => new ProduktionsomradeRadAggregat(x, raw));
        this.produktionsomraderader = orderBy(produktionsomradeRadAggregat, x => x.namn);
    }

    sumTransporterat = (period: IAggregatPeriod): number => this.produktionsomraderader.reduce((agg, item) => agg + item.sumTransporterat(period), 0);

    transporteratLeveransrad = (periods: IAggregatPeriod[]): number =>
        periods.reduce((agg, item) => agg + (item.isCurrentlyActive ? this.sumPlan(item) : this.sumTransporterat(item)), 0);

    sumPlan = (period: IAggregatPeriod): number => this.produktionsomraderader.reduce((agg, item) => agg + item.sumPlan(period), 0);

    calculateTakt = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumPlan, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);
}

export class ProduktionsomradeRadAggregat {

    kundorderrader: ProduktionsomradeKundorderradAggregat[];

    constructor(private readonly produktionsomrade: IProduktionsomrade, private readonly raw: IOrderProduktionsomraderad[]) {
        const rawProduktionsomraderader = raw.filter(x =>
            x.kundorderrad.forsorjningskallor.some(fk =>
                fk.id === produktionsomrade.id && fk.group === ForsorjningskallaTyp.Produktionsomrade));

        this.kundorderrader = rawProduktionsomraderader.map(por => new ProduktionsomradeKundorderradAggregat(produktionsomrade, por));
    }

    get namn(): string {
        return this.produktionsomrade.namn;
    }

    get id(): number {
        return this.produktionsomrade.id;
    }

    sumPlan = (period: IAggregatPeriod): number => this.kundorderrader.reduce((agg, item) => agg + item.sumPlan(period), 0);

    sumTransporterat = (period: IAggregatPeriod): number => this.kundorderrader.reduce((agg, item) => agg + item.sumTransporterat(period), 0);

    overenskommet = (): number => this.kundorderrader.reduce((agg, item) => agg + item.overenskommet, 0);

    transporteratLeveransrad = (periods: IAggregatPeriod[]): number => {
        const actives = periods.filter(x => x.isCurrentlyActive);
        let planerat = 0;
        actives.forEach(x => {
            planerat += this.sumPlan(x);
        });

        const nonActives = periods.filter(x => !x.isCurrentlyActive);
        let transporterat = 0;
        nonActives.forEach(x => {
            transporterat += this.sumTransporterat(x);
        });
        return planerat + transporterat;
    };

    avvikelse = (periods: IAggregatPeriod[]): number => this.transporteratLeveransrad(periods) - this.overenskommet();

    calculateTakt = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumPlan, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);
}

export class ProduktionsomradeKundorderradAggregat {
    private readonly forsorjningskalla: ForsorjningskallaAggregat;

    get kundorderId(): number {
        return this.produktionsomraderad.id;
    }

    get kundorderradId(): number {
        return this.produktionsomraderad.kundorderrad.id;
    }

    get produktionsomradeId(): number {
        return this.forsorjningskalla.id;
    }

    get kundNamn(): string {
        if (!this.produktionsomraderad.kund) {
            return "";
        }
        return this.produktionsomraderad.kund.namn;
    }

    get mattslag(): string {
        return this.produktionsomraderad.mattslag;
    }

    get mottagningsplats(): IOrderMottagningsplats {
        return this.produktionsomraderad.kundorderrad.mottagningsplats;
    }

    get handelssortiment(): IOrderHandelssortiment {
        return this.produktionsomraderad.kundorderrad.handelssortiment;
    }

    get overenskommet(): number {
        return this.forsorjningskalla.overenskommet;
    }

    set overenskommet(value: number) {
        this.forsorjningskalla.overenskommet = value;
    }

    get forsorjningskallaId(): number {
        return this.forsorjningskalla.id;
    }

    get periodFrom(): Date {
        return this.produktionsomraderad.kundorderrad.periodFrom;
    }

    get periodTom(): Date {
        return this.produktionsomraderad.kundorderrad.periodTom;
    }

    constructor(private readonly produktionsomrade: IProduktionsomrade, private readonly produktionsomraderad: IOrderProduktionsomraderad) {
        const rawForsorjningskalla = this.produktionsomraderad.kundorderrad.forsorjningskallor.filter(x =>
            x.id === this.produktionsomrade.id && x.group === ForsorjningskallaTyp.Produktionsomrade)[0];
        const leveransrader = new LeveransraderAggregat(produktionsomraderad.kundorderrad.leveransrader);

        this.forsorjningskalla = ForsorjningskallaAggregat.create(rawForsorjningskalla, leveransrader);
    }

    sumPlan = (period: IAggregatPeriod): number => this.forsorjningskalla.sumPlan(period);

    sumTransporterat = (period: IAggregatPeriod): number => this.forsorjningskalla.sumTransporterat(period);

    transporteratLeveransrad = (periods: IAggregatPeriod[]): number => {
        const actives = periods.filter(x => x.isCurrentlyActive);
        let planerat = 0;
        actives.forEach(x => {
            planerat += this.sumPlan(x);
        });

        const nonActives = periods.filter(x => !x.isCurrentlyActive);
        let transporterat = 0;
        nonActives.forEach(x => {
            transporterat += this.sumTransporterat(x);
        });
        return planerat + transporterat;
    };

    avvikelse = (periods: IAggregatPeriod[]): number => this.transporteratLeveransrad(periods) - this.overenskommet;

    calculateTakt = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumPlan, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    updateForsorjningsradPlan(fordeladKvantitet: IDagKvantitet[]) {
        this.forsorjningskalla.updateForsorjningsradPlan(fordeladKvantitet);
    }
}

export class JlpProduktionsomradenSearchModel {
    kunder?: any[];
    omraden?: any[];
    mottagningsplatser?: any[];
    handelssortimentsgrupper?: any[];
    periodFrom: Date;
    periodTom: Date;

    constructor() {
        this.kunder = [];
        this.omraden = [];
        this.mottagningsplatser = [];
        this.handelssortimentsgrupper = [];
        this.periodFrom = null;
        this.periodTom = null;
    }
}
